import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { venueUrl } from "../utils/venueUrl";
import { API } from "../App";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Calendar, MapPin, Clock, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { useFormatDate } from "../utils/dateFormat";

export default function EventDetailPage() {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = useFormatDate();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${eventSlug}`);
        setEvent(response.data);
      } catch (error) {
        console.error("Error fetching event:", error);
        navigate("/events");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventSlug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 animate-pulse">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-muted h-80 rounded-xl mb-8" />
            <div className="bg-muted h-8 w-1/3 rounded mb-4" />
            <div className="bg-muted h-4 w-1/2 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!event) return null;

  const startDate = new Date(event.start_datetime);
  const endDate = event.end_datetime ? new Date(event.end_datetime) : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Image */}
          {event.image_url && (
            <div className="relative aspect-[2/1] rounded-2xl overflow-hidden mb-8" data-testid="event-image">
              <img
                src={event.image_url}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Event Info */}
          <div className="space-y-6">
            {/* Date & Type Badge */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                {event.event_type_name}
              </span>
              {event.is_featured && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  Featured
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-foreground tracking-tight" data-testid="event-title">
              {event.title}
            </h1>

            {/* Date & Time */}
            <div className="flex flex-col sm:flex-row gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <span>{fmt(startDate, "EEEE, MMMM d, yyyy")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <span>
                  {fmt(startDate, "h:mm a")}
                  {endDate && ` - ${fmt(endDate, "h:mm a")}`}
                </span>
              </div>
            </div>

            {/* Venue Link */}
            {event.venue_name && (
              <Link
                to={venueUrl({slug: event.venue_slug, district_slug: event.district_slug, area_slug: event.area_slug})}
                className="flex items-center gap-3 p-4 bg-muted rounded-xl hover:bg-muted/80 transition-colors"
                data-testid="event-venue-link"
              >
                <MapPin className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium text-foreground">{event.venue_name}</p>
                  <p className="text-sm text-muted-foreground">View venue details</p>
                </div>
              </Link>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {event.reservation_required && event.venue_slug && (
                <Button asChild data-testid="event-book-button">
                  <Link to={venueUrl({slug: event.venue_slug, district_slug: event.district_slug, area_slug: event.area_slug})}>
                    Make a Reservation
                  </Link>
                </Button>
              )}
              {event.external_ticket_url && (
                <Button asChild variant="outline" data-testid="event-external-link">
                  <a href={event.external_ticket_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Get Tickets
                  </a>
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success("Link copied!");
                }}
                data-testid="event-share-button"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>

            {/* Description */}
            {(event.description_long || event.description_short) && (
              <div className="pt-6 border-t border-border" data-testid="event-description">
                <h2 className="text-xl font-medium text-foreground mb-4">About this event</h2>
                <div className="prose prose-neutral max-w-none">
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {event.description_long || event.description_short}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
